import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Select,
  Textarea,
  cn,
  useToast,
} from '@adop/ui';
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Eye,
  Globe,
  Monitor,
  RefreshCw,
  Smartphone,
  Upload,
} from 'lucide-react';
import {
  DEFAULT_ACCENT_COLOR,
  DEFAULT_PRIMARY_COLOR,
  FONTS,
  isFontName,
  normalizeBranding,
} from '@adop/shared';
import type {
  Branding,
  Location,
  PreviewLinkResponse,
  PublishResponse,
} from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAuth } from '../../lib/auth-context';
import { useAsync } from '../../lib/useAsync';
import { fetchMenu, getLocation, listOpeningHours, listPrinterDevices, updateLocationBranding } from '../../lib/data';
import { postJson, errorMessage } from '../../lib/api';
import { computePublishChecklist, isReadyToPublish } from '../../lib/checklist';
import { Alert } from '../../components/Alert';
import { previewUrlFor, storefrontUrlFor } from '../../lib/storefront';
import { PageHeader } from '../../components/PageHeader';
import { ImageUpload } from '../../components/ImageUpload';
import { LoadingBlock } from '../../components/LoadingState';
import { postStorefrontPreview, useDebouncedEffect } from '../../lib/preview';

interface BrandingData {
  location: Location;
  hoursCount: number;
  itemCount: number;
  printerCount: number;
}

export function BrandingPage() {
  const { location: workspaceLocation, restaurant, canOwn } = useWorkspace();
  const { refresh } = useAuth();
  const { toast } = useToast();
  const state = useAsync<BrandingData | null>(async () => {
    if (!workspaceLocation) return null;
    const [rawLocation, hours, menu, devices] = await Promise.all([
      getLocation(workspaceLocation.id),
      listOpeningHours(workspaceLocation.id),
      fetchMenu(workspaceLocation.id),
      listPrinterDevices(workspaceLocation.id),
    ]);
    if (!rawLocation) return null;
    return {
      location: rawLocation as unknown as Location,
      hoursCount: hours.length,
      itemCount: menu.items.length,
      printerCount: devices.filter((device) => !device.revoked_at).length,
    };
  }, [workspaceLocation?.id]);

  const location = state.data?.location ?? null;

  const [branding, setBranding] = useState<Branding>({
    primary_color: DEFAULT_PRIMARY_COLOR,
    accent_color: DEFAULT_ACCENT_COLOR,
    font: 'inter',
  });
  const [about, setAbout] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [previewReload, setPreviewReload] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!location) return;
    setBranding(normalizeBranding(location.branding));
    setAbout(location.about ?? '');
  }, [location]);

  const locationId = workspaceLocation?.id;
  const locationSlug = location?.slug;
  const loadPreview = useCallback(async () => {
    if (!locationId || !locationSlug) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await postJson<PreviewLinkResponse>(
        `/api/platform/locations/${locationId}/preview-link`,
      );
      setPreviewUrl(previewUrlFor(locationSlug, response.url));
    } catch (cause) {
      setPreviewUrl(null);
      setPreviewError(errorMessage(cause));
    } finally {
      setPreviewLoading(false);
    }
  }, [locationId, locationSlug]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  // Push the in-progress form state into the real storefront preview so owners
  // see their edits before saving.
  useDebouncedEffect(
    () => {
      postStorefrontPreview(iframeRef.current, previewUrl, {
        name: location?.name ?? '',
        about: about.trim() || null,
        branding,
      });
    },
    250,
    [previewUrl, previewReload, location?.name, about, branding],
  );

  if (!workspaceLocation) {
    return <EmptyState icon={<Globe className="h-6 w-6" />} title="Select a location" />;
  }

  if (state.loading || !location || !state.data) {
    return <LoadingBlock />;
  }

  const checklist = computePublishChecklist({
    hasBranding: Boolean(branding.logo_key) || Boolean(branding.primary_color),
    hasHours: state.data.hoursCount > 0,
    itemCount: state.data.itemCount,
    payInStoreEnabled: location.pay_in_store_enabled,
    stripeChargesEnabled: restaurant?.stripe_charges_enabled ?? false,
    reservationsEnabled: location.reservations_enabled,
    printerCount: state.data.printerCount,
  });
  const ready = isReadyToPublish(checklist, location.ordering_enabled);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateLocationBranding(location.id, branding, about.trim() || null);
      toast({ title: 'Branding saved', variant: 'success' });
      // Reload the iframe so it renders the state we just persisted.
      setPreviewReload((value) => value + 1);
      state.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const response = await postJson<PublishResponse>(`/api/platform/locations/${location.id}/publish`);
      toast({ title: 'Published', description: response.storefront_url, variant: 'success' });
      state.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async () => {
    setPublishing(true);
    setError(null);
    try {
      await postJson(`/api/platform/locations/${location.id}/unpublish`);
      toast({ title: 'Storefront unpublished', variant: 'success' });
      state.reload();
      void refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPublishing(false);
    }
  };

  const preview = async () => {
    setError(null);
    try {
      const response = await postJson<PreviewLinkResponse>(
        `/api/platform/locations/${location.id}/preview-link`,
      );
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Branding & site"
        description="Your colours, fonts and photos appear on your public storefront."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => void preview()}>
              <Eye className="h-4 w-4" /> Preview unpublished site
            </Button>
            {canOwn ? (
              location.published ? (
                <Button variant="outline" size="sm" loading={publishing} onClick={() => void unpublish()}>
                  Unpublish
                </Button>
              ) : (
                <Button size="sm" loading={publishing} disabled={!ready} onClick={() => void publish()}>
                  <Upload className="h-4 w-4" /> Publish
                </Button>
              )
            ) : null}
          </div>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <div className="lg:hidden">
        <Button variant="outline" size="sm" onClick={() => setShowPreview((value) => !value)}>
          <Eye className="h-4 w-4" /> {showPreview ? 'Hide live preview' : 'Show live preview'}
        </Button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <ImageUpload
                locationId={location.id}
                value={branding.logo_key ?? null}
                onChange={(key) => setBranding((current) => ({ ...current, logo_key: key }))}
                label="Logo"
              />
              <ImageUpload
                locationId={location.id}
                value={branding.cover_key ?? null}
                onChange={(key) => setBranding((current) => ({ ...current, cover_key: key }))}
                label="Cover photo"
              />
              <div className="grid grid-cols-2 gap-4">
                <label className="text-sm font-medium text-zinc-900">
                  Primary colour
                  <input
                    type="color"
                    value={branding.primary_color ?? DEFAULT_PRIMARY_COLOR}
                    onChange={(event) =>
                      setBranding((current) => ({ ...current, primary_color: event.target.value }))
                    }
                    className="mt-1 block h-10 w-full cursor-pointer rounded-lg border border-zinc-200"
                  />
                </label>
                <label className="text-sm font-medium text-zinc-900">
                  Accent colour
                  <input
                    type="color"
                    value={branding.accent_color ?? DEFAULT_ACCENT_COLOR}
                    onChange={(event) =>
                      setBranding((current) => ({ ...current, accent_color: event.target.value }))
                    }
                    className="mt-1 block h-10 w-full cursor-pointer rounded-lg border border-zinc-200"
                  />
                </label>
              </div>
              <Select
                label="Font"
                value={branding.font ?? 'inter'}
                onChange={(event) =>
                  setBranding((current) => ({
                    ...current,
                    font: isFontName(event.target.value) ? event.target.value : 'inter',
                  }))
                }
              >
                {FONTS.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.label}
                  </option>
                ))}
              </Select>
              <Textarea
                label="About"
                value={about}
                rows={4}
                onChange={(event) => setAbout(event.target.value)}
                hint="A short introduction shown on your storefront and marketplace card."
              />
              <div className="flex items-center gap-2">
                <Button loading={saving} onClick={() => void save()}>
                  Save branding
                </Button>
                <span className="text-sm text-zinc-500">
                  {location.published ? 'Published' : 'Not published'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Publish checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {checklist.map((item) => (
                  <li key={item.key} className="flex items-center gap-2">
                    {item.done ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Circle className="h-4 w-4 text-zinc-300" />
                    )}
                    <span className={item.done ? 'text-zinc-500' : 'text-zinc-900'}>{item.label}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-zinc-500">
                Publishing requires opening hours, at least one menu item and a payment method. Branding and a
                printer are recommended but not blocking.
              </p>
              <div className="mt-3">
                <Checkbox label="Ready to publish" checked={ready} readOnly />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={cn('space-y-5', showPreview ? 'block' : 'hidden lg:block')}>
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>Live preview</CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant={previewWidth === 'desktop' ? 'outline' : 'ghost'}
                    size="sm"
                    aria-pressed={previewWidth === 'desktop'}
                    title="Desktop width"
                    onClick={() => setPreviewWidth('desktop')}
                  >
                    <Monitor className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewWidth === 'mobile' ? 'outline' : 'ghost'}
                    size="sm"
                    aria-pressed={previewWidth === 'mobile'}
                    title="Mobile width"
                    onClick={() => setPreviewWidth('mobile')}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Refresh preview"
                    disabled={!previewUrl}
                    onClick={() => setPreviewReload((value) => value + 1)}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Open preview in new tab"
                    disabled={!previewUrl}
                    onClick={() =>
                      previewUrl && window.open(previewUrl, '_blank', 'noopener,noreferrer')
                    }
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {previewLoading ? (
                <LoadingBlock label="Loading storefront preview…" />
              ) : previewError ? (
                <div className="space-y-3">
                  <Alert>{previewError}</Alert>
                  <Button variant="outline" size="sm" onClick={() => void loadPreview()}>
                    <RefreshCw className="h-4 w-4" /> Retry
                  </Button>
                </div>
              ) : previewUrl ? (
                <div className="flex justify-center">
                  <div
                    className="w-full transition-[max-width] duration-200"
                    style={{ maxWidth: previewWidth === 'mobile' ? 390 : '100%' }}
                  >
                    <iframe
                      ref={iframeRef}
                      key={previewReload}
                      title="Storefront preview"
                      src={previewUrl}
                      className="h-[70vh] min-h-[420px] w-full rounded-lg border border-zinc-200 bg-white"
                    />
                  </div>
                </div>
              ) : null}
              <p className="mt-3 text-xs text-zinc-500">
                {storefrontUrlFor(location.slug)} — your real storefront. Edits preview instantly
                and are saved when you click “Save branding”.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
